import { createLogger } from '@sorrem/shared';
import { useEffect, useRef, type MutableRefObject, type RefObject } from 'react';
import {
    createRenderWorkerSession,
    type RenderWorkerSession,
    type RenderWorkerSessionOptions,
} from '@sorrem-client/game/render-manager';
import type { RenderWorkerSceneCommand } from '@sorrem-client/game/render-config';
import { registerCleanRenderSource } from '@sorrem-client/ui/snapshot/clean-render-sources';

// Not re-exported from the hooks barrel: it pulls in the render pipeline, and
// the barrel is imported from `game/` code, so import this module by path.

const logger = createLogger('client:use-render-worker-session');

/** The largest drawing buffer side every WebGPU device can make (its guaranteed `maxTextureDimension2D`). */
export const MAX_DRAWING_BUFFER_SIDE = 8192;

/**
 * The drawing buffer a canvas needs, in device pixels: its laid-out size
 * (`clientWidth`/`clientHeight`, which a CSS transform leaves alone, unlike
 * `getBoundingClientRect`) at `pixelRatio`. Character Home zooms its room by
 * a transform; measured with it, the preview asked for a buffer ten times
 * too big, past what the GPU can make, and lost its device. Scaled down to
 * `MAX_DRAWING_BUFFER_SIDE` keeping its shape, and never below one pixel.
 */
export function measureDrawingBuffer(canvas: Pick<HTMLCanvasElement, 'clientWidth' | 'clientHeight'>, pixelRatio: number): { width: number; height: number } {
    const width = canvas.clientWidth * pixelRatio;
    const height = canvas.clientHeight * pixelRatio;
    const fit = Math.min(1, MAX_DRAWING_BUFFER_SIDE / Math.max(width, height, 1));
    return {
        width: Math.max(1, Math.round(width * fit)),
        height: Math.max(1, Math.round(height * fit)),
    };
}

/** `TCommand`: the scene's own commands (its slice of `RenderWorkerSceneCommand`), so one meant for another scene doesn't compile. */
export interface UseRenderWorkerSessionOptions<TCommand extends RenderWorkerSceneCommand = RenderWorkerSceneCommand> {
    /**
     * The worker entry, imported with Vite's `?worker` suffix
     * (`import FooRenderWorker from './foo-render-worker-entry?worker'`) so
     * Vite bundles it in production too. A `new URL(...)` passed in here is
     * only an asset to Vite and ships as raw TypeScript. Changing it restarts
     * the session.
     */
    readonly worker: new () => Worker;
    /** Worker -> main-thread callbacks. Captured once when the session is created; read the latest values through refs (this hook never restarts the worker because a callback changed). */
    readonly callbacks?: RenderWorkerSessionOptions;
    /** Drawing-buffer pixels per CSS pixel. `device` (default) renders at the device pixel ratio for a sharp image; `css` renders one pixel per CSS pixel, which is cheaper. */
    readonly resolution?: 'device' | 'css';
    /** Class applied to the canvas element the hook creates. */
    readonly canvasClassName?: string;
    /** Called once the session exists, before any other effect of the caller runs. */
    readonly onSessionCreated?: (session: RenderWorkerSession<TCommand>) => void;
    /** An element the caller already renders (a parent's) to create the canvas in, instead of one it renders for the returned `containerRef`. */
    readonly containerRef?: RefObject<HTMLDivElement>;
    /** Offers the scene to developer snapshots' clean renders while it is mounted (#303). Read once, when the session is created. */
    readonly cleanRenderSource?: boolean;
}

export interface RenderWorkerSessionHandle<TCommand extends RenderWorkerSceneCommand = RenderWorkerSceneCommand> {
    /** Attach to the element the canvas is created inside. It must have a size (the canvas fills it). Listen for pointer input on this element, not on the canvas, which is replaced on every mount. */
    readonly containerRef: RefObject<HTMLDivElement>;
    /** The live session, or null before mount / after unmount / if creation failed. Send commands through this from effects declared after the hook call. */
    readonly sessionRef: MutableRefObject<RenderWorkerSession<TCommand> | null>;
}

/**
 * Runs a render worker against a canvas the hook creates inside a container.
 *
 * The worker renders straight into the canvas (`transferControlToOffscreen()`),
 * so the main thread does no per-frame work. That has two consequences this
 * hook exists to handle in one place:
 * - a canvas can be transferred only once, and React (StrictMode) re-runs
 *   effects, so every mount gets a fresh canvas element, removed on unmount;
 * - after the transfer the main thread can no longer set `canvas.width`/
 *   `height`, so the drawing-buffer size is measured before the transfer and
 *   afterwards only ever sent to the worker (`session.resize`), driven by a
 *   `ResizeObserver` plus window resizes (which also cover devicePixelRatio
 *   changes such as browser zoom).
 *
 * Call this before any `useEffect` that sends commands: effects run in
 * declaration order, and those need `sessionRef` to be set.
 */
export function useRenderWorkerSession<TCommand extends RenderWorkerSceneCommand = RenderWorkerSceneCommand>(options: UseRenderWorkerSessionOptions<TCommand>): RenderWorkerSessionHandle<TCommand> {
    const ownContainerRef = useRef<HTMLDivElement>(null);
    const containerRef = options.containerRef ?? ownContainerRef;
    const sessionRef = useRef<RenderWorkerSession<TCommand> | null>(null);
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const WorkerConstructor = options.worker;
    const { resolution = 'device', canvasClassName } = options;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const { callbacks, onSessionCreated, cleanRenderSource } = optionsRef.current;

        const canvas = document.createElement('canvas');
        canvas.style.display = 'block';
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        if (canvasClassName) {
            canvas.className = canvasClassName;
        }
        container.prepend(canvas);

        const measure = (): { width: number; height: number } => measureDrawingBuffer(canvas, resolution === 'device' ? window.devicePixelRatio || 1 : 1);

        // The last time the main thread may set the size: from here on it is the worker's.
        const initial = measure();
        canvas.width = initial.width;
        canvas.height = initial.height;

        let session: RenderWorkerSession<TCommand>;
        try {
            session = createRenderWorkerSession<TCommand>(canvas, () => new WorkerConstructor(), callbacks);
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            logger.error('failed to create render worker session:', error);
            callbacks?.onError?.(`Failed to initialize the renderer: ${message}`);
            canvas.remove();
            return;
        }
        sessionRef.current = session;
        onSessionCreated?.(session);
        const unregisterCleanRender = cleanRenderSource
            ? registerCleanRenderSource({ isShown: () => canvas.isConnected, capture: (scale) => session.captureCleanRender(scale) })
            : (): void => undefined;

        const handleResize = (): void => {
            const next = measure();
            if (next.width === session.size.width && next.height === session.size.height) return;
            session.resize(next.width, next.height);
        };
        window.addEventListener('resize', handleResize);
        const resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(canvas);

        return (): void => {
            window.removeEventListener('resize', handleResize);
            resizeObserver.disconnect();
            unregisterCleanRender();
            session.dispose();
            sessionRef.current = null;
            canvas.remove();
        };
    }, [WorkerConstructor, resolution, canvasClassName, containerRef]);

    return { containerRef, sessionRef };
}
